import { useContext, useEffect, useState } from "react";
import styled from "styled-components";
import { useLogin } from "./useLogin";
import Logo from "../../interface/Logo";
import SpinnerMini from "../../interface/SpinnerMini";
import { useNavigate } from "react-router-dom";
import { useFetchUserDetails } from "../../hooks/useFetchUserDetails";
import toast from "react-hot-toast";
import { getAuth, sendPasswordResetEmail } from "firebase/auth";
import firebase from "../../services/firebase";
import { useRetrieveUser } from "./entra/useRetrieveUser";
import { UserContext } from "../../contexts/UserContext";
import { useAzureAuth } from "./entra/useAzureAuth";

const USE_ENTRA_AUTH = import.meta.env.VITE_USE_ENTRA_AUTH === "true";

const defaultEmail = import.meta.env.DEV
  ? import.meta.env.VITE_APP_DEFAULT_EMAIL
  : "";
const defaultPassword = import.meta.env.DEV
  ? import.meta.env.VITE_APP_DEFAULT_PASSWORD
  : "";

const ResponsiveWrapper = styled.div`
  @media (max-width: 768px) {
    & > * {
      transform: scale(0.75);
    }
  }
  @media (min-width: 769px) {
    & > * {
      transform: scale(1);
    }
  }
`;

const GlassForm = styled.form`
  height: 55rem;
  width: 40rem;
  background-color: rgba(255, 255, 255, 0.13);
  top: 50%;
  left: 50%;
  border-radius: 10px;
  backdrop-filter: blur(10px);
  border: 2px solid rgba(255, 255, 255, 0.1);
  box-shadow: var(--shadow-main);
  padding: 50px 35px;
`;

const StyledInput = styled.input`
  height: 50px;
  width: 100%;
  padding-left: 1em;
  background-color: var(--color-grey-100);
  &:hover {
    background-color: var(--color-grey-300);
  }
`;

const StyledLabel = styled.label`
  display: block;
  margin-top: 30px;
  font-size: 16px;
  font-weight: 500;
`;

const StyledButton = styled.button`
  margin-top: 50px;
  width: 100%;
  background-color: var(--color-grey-0);
  color: var(--color-grey-800);
  padding: 15px 0;
  font-size: 18px;
  font-weight: 600;
  border-radius: 5px;
  cursor: pointer;
`;

const NButton = styled.button`
  color: var(--color-grey-900);
  padding: 0.7em 1.7em;
  margin-top: 3em;
  font-size: 1.2em;
  width: 100%;
  border-radius: 0.5em;
  background: var(--color-grey-0);
  border: 1px solid var(--color-grey-100);
  transition: all 0.3s;
  box-shadow: var(--shadow-btn);
  &:active {
    color: var(--color-grey-300);
    box-shadow: var(--shadow-btn-active);
  }
`;

function LoginForm({ onClose, redirectPath }) {
  const { setUserDetails } = useContext(UserContext);
  const [email, setEmail] = useState(defaultEmail);
  const [password, setPassword] = useState(defaultPassword);
  // const [isRemaUser, setIsRemaUser] = useState(false); // NEW: Track if email is @rema.no
  const [currentDialog, setCurrentDialog] = useState("login");
  const { login, isLoading } = useLogin();
  const fetchUserDetails = useFetchUserDetails();
  const navigate = useNavigate();
  const auth = getAuth(firebase);
  const { loginWithAzure } = useAzureAuth();
  const { error } = useRetrieveUser(email);
  const isCloud = import.meta.env.VITE_LOCATION === "cloud";
  

  const getAuthProvider = (email) => {
    const e = email.trim().toLowerCase();

    if (e.endsWith("@rema.no")) return "rema";

    if (e.endsWith("@wioss.no")) return "wioss";

    return "firebase";
  };

  useEffect(() => {
    if (email.trim().toLowerCase().endsWith("@rema.no")) {
      // setIsRemaUser(true);
      setPassword(""); // Clear password field
    } else {
      // setIsRemaUser(false);
    }
  }, [email]);

  const handleEmailChange = (e) => {
    setEmail(e.target.value);
  };

  const handleBlur = () => {
    // Handles autofill cases where onChange doesn't fire
    if (email.trim().toLowerCase().endsWith("@rema.no")) {
      // setIsRemaUser(true);
      setPassword(""); // Ensure password is cleared
    } else {
      // setIsRemaUser(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;

    try {
      const provider = getAuthProvider(email);

      if (USE_ENTRA_AUTH && provider !== "firebase") {
        console.log("Azure Entra login detected...");

        const result = await loginWithAzure(provider);
        if (!result) {
          toast.error("Azure login failed. Please try again.");
          return;
        }

        const { verifiedEmail, userDetails } = result;
        toast.success(
          userDetails
            ? `Welcome, ${userDetails.userName}!`
            : "Login successful!"
        );

        if (redirectPath) navigate(redirectPath);
        onClose?.();
      } else {
        login(
          { email, password },
          {
            onSettled: () => setPassword(""),
            onSuccess: () => {
              fetchUserDetails(email);
              if (redirectPath) navigate(redirectPath);
              onClose?.();
            },
            onError: (error) => {
              console.error("Login failed:", error.message);
              toast.error(`Login failed: ${error.message}`);
            },
          }
        );
      }
    } catch (error) {
      toast.error(`Login failed: ${error.message}`);
    }
  };

  return (
    <ResponsiveWrapper>
      <GlassForm onSubmit={handleSubmit}>
        <Logo size="5rem" />
        <h3>Login Here</h3>

        {/* Email Input */}
        <StyledLabel htmlFor="email">Email address</StyledLabel>
        <StyledInput
          type="email"
          id="email"
          autoComplete="username"
          value={email}
          onChange={handleEmailChange}
          onBlur={handleBlur} // Detects autofill
          disabled={isLoading}
        />

        {/* Password Input - HIDDEN for rema.no users */}
        {getAuthProvider(email) === "firebase" && (
          <>
            <StyledLabel htmlFor="password">Password</StyledLabel>
            <StyledInput
              type="password"
              id="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={isLoading}
            />
          </>
        )}

        {/* Login Button */}
        <NButton disabled={isLoading}>
          {!isLoading ? "Login" : <SpinnerMini />}
        </NButton>
      </GlassForm>
    </ResponsiveWrapper>
  );
}

export default LoginForm;
